import { flagKey, type RegionCode, type RuntimeEnvironment } from '@planner/config';
import { useEffect, useState } from 'react';
import { posthogClient } from './analytics/index.js';

type FlagState = 'checking' | 'held' | 'available';
const FLAG_TIMEOUT_MS = 6_000;

export function useComingSoonFlag(
  regionCode: RegionCode,
  environment: RuntimeEnvironment,
): FlagState {
  const [state, setState] = useState<FlagState>(
    import.meta.env.VITE_POSTHOG_KEY ? 'checking' : 'available',
  );

  useEffect(() => {
    let timeout: number | undefined;
    try {
      const client = posthogClient();
      if (!client) {
        if (environment !== 'local') {
          console.warn('PostHog is not configured; the coming-soon flag is unavailable.');
        }
        setState('available');
        return;
      }
      const key = flagKey('coming-soon', environment === 'local' ? 'dev' : environment, regionCode);
      timeout = window.setTimeout(() => {
        console.warn('Coming-soon flag did not load; continuing with the public region policy.');
        setState('available');
      }, FLAG_TIMEOUT_MS);
      const unsubscribe = client.onFeatureFlags((_flags, _variants, context) => {
        window.clearTimeout(timeout);
        if (context?.errorsLoading) {
          console.warn(
            'Coming-soon flag could not be loaded; continuing with the public region policy.',
          );
          setState((current) => (current === 'checking' ? 'available' : current));
          return;
        }
        setState(client.isFeatureEnabled(key, { send_event: false }) ? 'held' : 'available');
      });
      return () => {
        window.clearTimeout(timeout);
        unsubscribe();
      };
    } catch (error) {
      window.clearTimeout(timeout);
      console.error('Could not initialise the coming-soon flag', error);
      setState('available');
    }
  }, [environment, regionCode]);

  return state;
}
